import { DebugLogger } from "./debugLogger.js";
import { ensurePersistentStorage } from "./persistence.js";

// Ships device diagnostics (debug logs + storage/persistence state) to the
// server when online, so incidents on field tablets are diagnosable without
// physical access. Throttled to once per day, except that new ERROR-level
// logs are reported immediately. Fire-and-forget: failures (offline) simply
// retry on a later page load.
const LAST_SENT_KEY = "artemis_diagnostics_last_sent_at";
const SEND_INTERVAL_MS = 24 * 60 * 60 * 1000;
const MAX_LOG_ENTRIES = 200;

export async function reportDiagnosticsIfDue(farmId) {
  if (!farmId) return false;

  const now = Date.now();
  const logs = DebugLogger.getLogs();
  if (!isReportDue(logs, now)) return false;

  try {
    const [persistence, storageInfo] = await Promise.all([
      ensurePersistentStorage(),
      DebugLogger.getStorageInfo(),
    ]);

    const response = await fetch(`/farms/${farmId}/client_diagnostics`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({
        payload: {
          persisted: persistence.persisted,
          persistence_supported: persistence.supported,
          storage: storageInfo,
          user_agent: navigator.userAgent,
          logs: logs.slice(0, MAX_LOG_ENTRIES),
        },
      }),
    });

    if (!response.ok) return false;

    localStorage.setItem(LAST_SENT_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function isReportDue(logs, now) {
  const lastSent = lastSentAt();
  if (now - lastSent >= SEND_INTERVAL_MS) return true;

  return logs.some(
    (entry) =>
      entry.level === "ERROR" && Date.parse(entry.timestamp) > lastSent,
  );
}

function lastSentAt() {
  const parsed = Number(localStorage.getItem(LAST_SENT_KEY));
  return Number.isFinite(parsed) ? parsed : 0;
}
